import { ScreenerFilter } from '@api/api/screener';
import { FilterOption } from '@terminal/components/update-filter-options/updateFilterOptions';
import { RefObject, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';

export const DEFAULT_EPISODES_TO_SHOW = 10;

export const EMPTY_FILTER_LABELS = {
  searchPlaceholder: '',
  resetLabel: '',
  noResultsLabel: '',
  selectedLabel: '',
  disabledTooltipLabel: '',
  multiselectFeatured: '',
  remove: '',
  setCustomRange: '',
} as const;

interface UseCollapsibleEpisodesTableReturn {
  isCollapsed: boolean;
  tableRef: RefObject<HTMLDivElement | null>;
  activeSortOrderOption: FilterOption;
  setActiveSortOrderOption: (option: FilterOption) => void;
  sortOrderFilter: ScreenerFilter;
  handleViewMoreClick: () => void;
}

export const useCollapsibleEpisodesTable =
  (): UseCollapsibleEpisodesTableReturn => {
    const { t } = useTranslation('widget');
    const tableRef = useRef<HTMLDivElement>(null);
    const [isCollapsed, setIsCollapsed] = useState(true);
    const sortOrderOptions = useMemo(
      () => [
        {
          key: 'desc',
          name: t('pastEpisode.table.sortNewestFirst'),
          value: { id: 'desc' },
        },
        {
          key: 'asc',
          name: t('pastEpisode.table.sortOldestFirst'),
          value: { id: 'asc' },
        },
      ],
      [t]
    );
    const [activeSortOrderOption, setActiveSortOrderOption] =
      useState<FilterOption>(sortOrderOptions[0]);

    const sortOrderFilter = useMemo(
      () => ({
        key: 'sortOrder',
        name: t('pastEpisode.table.sort'),
        searchable: false,
        category: 'Sort Order',
        locked: true,
        visible: true,
        config: {
          name: t('pastEpisode.table.sort'),
          multiselect: false,
          options: sortOrderOptions,
        },
      }),
      [t, sortOrderOptions]
    );

    const handleViewMoreClick = () => {
      setIsCollapsed(!isCollapsed);

      setTimeout(() => {
        const rows = tableRef.current?.querySelectorAll('tbody tr');

        if (isCollapsed && rows?.[DEFAULT_EPISODES_TO_SHOW - 1]) {
          rows[DEFAULT_EPISODES_TO_SHOW - 1].scrollIntoView({
            behavior: 'smooth',
            block: 'start',
          });
        }
      }, 0);
    };

    return {
      isCollapsed,
      tableRef,
      activeSortOrderOption,
      setActiveSortOrderOption,
      sortOrderFilter,
      handleViewMoreClick,
    };
  };
